import {useEffect, useState} from 'react';

import {view, Action} from '@sail/ui';
import {useIntl} from 'react-intl';
import {Conversation} from '@stripe-internal/conversations-client';
import {noTranslateMessages} from '../helpers/noTranslate';
import {widgetLinkAction} from '../helpers/widgetLinkAction';
import {DevBadge} from '../Chat/DevBadge';

const QA_ADMIN_URL = 'https://admin.qa.corp.stripe.com';
const PROD_ADMIN_URL = 'https://admin.corp.stripe.com';
const CONVERSATION_PATH = '/universal-help/conversation-details/v2/';

const devMessages = noTranslateMessages({
  normalRoutingModeLabel: {
    id: 'support_conversations_client.header.dev_badge.normal_routing_mode_label',
    defaultMessage: 'Routing mode: normal',
  },
  testQueueRoutingModeLabel: {
    id: 'support_conversations_client.header.dev_badge.test_queue_routing_mode_label',
    defaultMessage: 'Routing mode: test queue',
  },
  conversationTranscriptLabel: {
    id: 'support_conversations_client.header.dev_badge.conversation_transcript_label',
    defaultMessage: 'Transcript',
  },
  forestVersionLabel: {
    id: 'support_conversations_client.header.dev_badge.forest_version_label',
    defaultMessage: 'Forest version: {forestVersion}',
  },
  testAgentModeLabel: {
    id: 'support_conversations_client.header.dev_badge.test_agent_mode_label',
    defaultMessage: 'Test agent mode: {testAgentMode}',
  },
});

function generateTranscriptLink(url: URL, conversationId?: string) {
  const host = url.host.toLowerCase();
  if (host.endsWith('-mydev.dev.stripe.me')) {
    const adminOrigin = url.origin.replace(
      /--support_conversations-/,
      '--admin-',
    );
    return `${adminOrigin}${CONVERSATION_PATH}${conversationId}`;
  }
  if (host.startsWith('qa-support')) {
    return `${QA_ADMIN_URL}${CONVERSATION_PATH}${conversationId}`;
  }
  return `${PROD_ADMIN_URL}${CONVERSATION_PATH}${conversationId}`;
}

export function ConversationDevBadge({
  conversation,
}: {
  conversation: Conversation;
}) {
  const conversationId = conversation.id;
  const [dev, setDev] = useState<boolean>(!!conversation.context.dev);
  const [synthetic, setSynthetic] = useState<boolean>(
    !!conversation.context.synthetic,
  );

  const intl = useIntl();
  const url = new URL(window.location.href);
  const isTestQueue = dev || synthetic;
  const urlSearchParams = new URLSearchParams(window.location.search);
  const forestVersion = urlSearchParams.get('forestVersion') || 'off';
  const testAgentMode = urlSearchParams.get('test_agent_mode') || 'off';

  const transcriptLink = generateTranscriptLink(url, conversationId);

  useEffect(() => {
    const sub = conversation.contextObservable().subscribe(() => {
      setDev(!!conversation.context.dev);
      setSynthetic(!!conversation.context.synthetic);
    });
    return () => sub.unsubscribe();
  }, [conversation]);

  return (
    <DevBadge dev={dev} synthetic={synthetic}>
      <view.div css={{stack: 'y', gap: 'small'}}>
        {conversationId ? (
          <Action href={transcriptLink} uses={[widgetLinkAction()]}>
            {intl.formatMessage(devMessages.conversationTranscriptLabel)}
          </Action>
        ) : null}
        <view.div>
          {isTestQueue
            ? intl.formatMessage(devMessages.testQueueRoutingModeLabel)
            : intl.formatMessage(devMessages.normalRoutingModeLabel)}
        </view.div>
        <view.div>
          {intl.formatMessage(devMessages.forestVersionLabel, {
            forestVersion,
          })}
        </view.div>
        <view.div>
          {intl.formatMessage(devMessages.testAgentModeLabel, {
            testAgentMode,
          })}
        </view.div>
      </view.div>
    </DevBadge>
  );
}
